import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { SWRConfig } from 'swr';
import { getFilteredUrl } from '../../lib/product';
import Accordion from '../common/accordion';
import { Container } from '../layout/container';
import Detail from '../product/detail';
// import { Aroma } from './aroma';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useTranslation } from '../../lib/i18n';
import { Attributes, StructureIcon } from './attributes';
import { Histogram } from './histogram';

// const DynamicRatingForm = dynamic(() => import('./rating-form'));
const DynamicProductImageCarousel = dynamic(
  () => import('../product/product-image-carousel'),
);

const SIZE = 142;
const MAX = 5;

const FAKTOR = SIZE / 2 / MAX;

export const Aroma = ({
  wine: { attributes },
  className = '',
  bgColor = 'bg-green',
}) => {
  if (!attributes['fruchtig-mineralisch'].value) {
    return <></>;
  }
  const { t } = useTranslation();
  const fm = parseInt(attributes['fruchtig-mineralisch'].value);
  const lt = parseInt(attributes['lieblich-trocken'].value);

  const translate = `translate(${fm * FAKTOR}px, ${-74 - lt * FAKTOR}px)`;

  return (
    <>
      <Link
        href={getFilteredUrl(
          `lieblich-trocken--${lt - 1}f-t${lt + 1}||fruchtig-mineralisch--${
            fm - 1
          }f-t${fm + 1}||weintyp--${encodeURIComponent(
            attributes.weintyp.value,
          )}`,
        )}
        aria-label={`Weine mit dem gleichen Aroma`}
      >
        <div
          className={`${className} relative px-7 text-center`}
          role='figure'
          aria-labelledby='aroma-cross'
        >
          <label className='display-block mb-1 text-sm relative bottom-[8px]'>
            {t('lovely')}
          </label>

          <label className='aromaCrossLabelLeft block h-0 text-sm relative right-[10px]'>
            {t(`${attributes['farbe'].valueSlug}EarthyMineral`)}
          </label>
          <label className='aromaCrossLabelRight block h-0  text-sm relative right-[10px]'>
            {t('fruity')}
          </label>

          <svg
            width='142'
            height='142'
            viewBox='0 0 142 142'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M69.0693 69.9014C0.256502 69.9014 0 70.4491 0 70.4491C0 70.4491 0.256502 70.9968 69.0693 70.9968C137.882 70.9968 142 70.4491 142 70.4491C142 70.4491 137.875 69.9014 69.0693 69.9014Z'
              fill='currentColor'
            />
            <path
              d='M71.5514 69.0693C71.5514 0.256502 71.0037 0 71.0037 0C71.0037 0 70.4561 0.256502 70.4561 69.0693C70.4561 137.882 71.0037 142 71.0037 142C71.0037 142 71.5514 137.875 71.5514 69.0693Z'
              fill='currentColor'
            />
          </svg>

          <div
            style={{ transform: translate }}
            className={`aromaCrossDot mx-auto h-[6px] w-[6px] rounded-full ${bgColor}`}
          ></div>

          <label className='-mt-1 block text-sm relative bottom-[8px]'>
            {t('dry')}
          </label>
        </div>
      </Link>
    </>
  );
};

const Structure = ({
  product,
  className = '',
  borderColor = 'border-green ',
  bgColor = 'bg-green',
  appellationImg,
}) => {
  const { valT } = useTranslation();
  return (
    <div className={`w-[400px] grid-cols-2 ${className} self-start `}>
      <Link
        href={getFilteredUrl(
          `appellation--${encodeURIComponent(
            product.attributes.appellation.value,
          )}`,
        )}
        className={`flex h-[200px] w-[200px] flex-col items-center border ${borderColor} px-1 py-2`}
      >
        <span className='mb-[15px] mt-[-15px]'>
          {valT(product.attributes, 'land')},{' '}
          {valT(product.attributes, 'appellation')}
        </span>
        {appellationImg && (
          <img
            alt=''
            className='max-h-[160px] max-w-[180px] flex-grow'
            src={appellationImg.replace('.svg', '.png')}
          />
        )}
      </Link>
      <div
        className={`flex h-[200px] w-[200px] flex-col justify-center border-b border-r border-t ${borderColor}`}
      >
        <Aroma
          wine={product}
          className='flex-grow mt-[-15px] '
          bgColor={bgColor}
        />
      </div>
      <Link
        href={getFilteredUrl(
          `strukturtext-v2--${product.attributes['strukturtext-v2'].value}`,
        )}
        className={`flex h-[40px] items-center justify-center gap-2 ${bgColor} text-sm text-white`}
      >
        <StructureIcon inverse wine={product} />
        <p className='mt-[-15px]'>
          {valT(product.attributes, 'strukturtext-v2')}
        </p>
      </Link>

      <div
        className={`flex h-[40px] flex-shrink items-center justify-center gap-2 border-b border-r border-black text-sm sm:border-green`}
      >
        <svg
          width='25'
          height='22'
          viewBox='0 0 25 22'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M17.6906 1.98462C17.037 1.35202 16.1621 0.999068 15.2517 1.00094H14.9268C14.2956 1.01447 13.6896 1.24884 13.2139 1.66342C12.9912 1.84082 12.7143 1.93793 12.4295 1.93793C12.1447 1.93793 11.8679 1.84129 11.6452 1.66342C11.1699 1.24697 10.563 1.01167 9.93086 1H9.60639C8.69507 0.999068 7.8197 1.35342 7.16656 1.98882L1.1146 8.05105C0.974541 8.19158 0.961002 8.41474 1.08425 8.57067L5.65719 14.3747C5.72115 14.4583 5.81546 14.5125 5.91957 14.5251C6.02368 14.5381 6.12872 14.5092 6.21136 14.4438C6.29399 14.3789 6.34675 14.2837 6.35795 14.1791C6.36962 14.0745 6.33881 13.97 6.27252 13.8887L1.91527 8.35872L7.71699 2.54906C8.22354 2.05792 8.90143 1.78433 9.60686 1.78527H9.9318C10.38 1.80067 10.8086 1.97155 11.1442 2.26847C11.5051 2.5682 11.9594 2.73207 12.4286 2.73207C12.8978 2.73207 13.352 2.5682 13.7129 2.26847C14.0495 1.97248 14.4786 1.80161 14.9268 1.78527H15.2517C15.9576 1.7834 16.6364 2.05652 17.1439 2.54719L23.0768 8.35125L17.5179 14.7739C17.6159 14.0367 17.6715 13.2949 17.6845 12.5516L21.5773 8.42314C21.658 8.33724 21.6963 8.21959 21.6804 8.10241C21.665 7.98523 21.5973 7.88158 21.497 7.81996L16.6607 4.84836C16.0799 4.45947 15.3946 4.25685 14.6961 4.26712H14.4361C13.951 4.27132 13.4762 4.40671 13.0621 4.65928C12.6681 4.86797 12.1966 4.86844 11.8021 4.66115C11.387 4.40718 10.9108 4.27179 10.4243 4.26899H10.1643C9.47474 4.25638 8.79778 4.4548 8.22354 4.83669L3.36162 7.81996C3.26311 7.88018 3.19681 7.98102 3.17907 8.09494C3.16133 8.20885 3.19541 8.32464 3.27105 8.41194L5.39621 10.855C5.53814 11.0184 5.78604 11.0357 5.94945 10.8933C6.11285 10.7509 6.13012 10.5035 5.98773 10.3396L4.16602 8.2462L8.65399 5.48937C9.10171 5.19524 9.6274 5.04258 10.1629 5.05145H10.4229C10.7675 5.05425 11.1046 5.15183 11.3978 5.33297C12.0392 5.69012 12.8198 5.69012 13.4613 5.33297C13.7545 5.15369 14.0906 5.05659 14.4347 5.05238H14.6947C15.2405 5.04258 15.776 5.20038 16.2293 5.50431L20.6753 8.23546L17.678 11.4143C17.6551 10.6748 17.6038 10.1594 17.5902 10.0352C17.5744 8.87507 16.3353 7.93527 14.8143 7.93527C13.6625 7.93527 12.8656 8.14723 12.43 8.5674C11.9944 8.14723 11.1975 7.93527 10.0457 7.93527C8.52514 7.93527 7.28561 8.8746 7.26974 10.0352C7.21745 10.5091 6.61846 16.5414 9.13626 19.4561C9.95374 20.423 11.1512 20.9865 12.4174 21H12.4435C13.7092 20.9865 14.9062 20.4239 15.7242 19.4575C16.4595 18.5378 16.973 17.4612 17.2247 16.3108L23.9116 8.58515C24.0474 8.42828 24.0376 8.19345 23.8892 8.04825L17.6906 1.98462ZM15.1285 18.9444C14.4632 19.7488 13.4734 20.2147 12.4291 20.2147C11.3847 20.2147 10.3949 19.7488 9.72964 18.9444C7.38879 16.2347 8.04427 10.1636 8.05127 10.1029C8.05314 10.0884 8.0536 10.0735 8.0536 10.0586C8.0536 9.3326 8.96586 8.7196 10.0457 8.7196C12.0378 8.7196 12.0378 9.34193 12.0378 9.54689V13.4662C12.0378 13.6828 12.2134 13.8584 12.43 13.8584C12.6466 13.8584 12.8222 13.6828 12.8222 13.4662V9.54735C12.8222 9.3424 12.8222 8.72007 14.8143 8.72007C15.8941 8.72007 16.8064 9.3326 16.8064 10.059C16.8064 10.074 16.8073 10.0884 16.8087 10.1034C16.8138 10.1468 17.1486 13.2491 16.4894 16.0074C16.4828 16.0284 16.4782 16.0494 16.4749 16.0708C16.2559 17.1204 15.7951 18.1045 15.1294 18.9449H15.1285V18.9444Z'
            fill='currentColor'
            stroke='currentColor'
            strokeWidth='0.3'
          />
        </svg>
        <p className='mt-[-15px]'>
          {valT(product.attributes, 'zwei-wort-beschrieb')}
        </p>
      </div>
    </div>
  );
};

const Title = ({ product, className = '' }) => {
  const { attrT, valT } = useTranslation();
  const origin = valT(product.attributes, 'ursprungsbezeichnung');
  return (
    <div className={`${className} text-green mt-[-100px]`}>
      <h1 className={`lg:text-[6.2rem] lg:leading-[6.2rem]`}>
        {attrT(product, 'name')}
        {origin ? <>&nbsp;{origin}</> : <></>}
      </h1>

      <Link
        href={'/' + product.attributes.produzent.valueSlug}
        className='mb-2 lg:mb-16 mt-[3rem] block text-xl lg:text-[3rem] font-bold uppercase '
      >
        {valT(product.attributes, 'produzent')}
      </Link>

      <div className='-ml-[2px] block lg:hidden'>
        {valT(product.attributes, 'land')},{' '}
        {valT(product.attributes, 'appellation')}
      </div>

      <Structure
        product={product}
        className='hidden lg:grid'
        appellationImg={product.regionMap?.srcGreen}
      />
    </div>
  );
};

function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
}

const WineDetail = ({ product, preview, related }) => {
  const router = useRouter();

  const [capture, setcapture] = useState(false);
  const [progressbar, setprogressbar] = useState('w-[8%]');

  const { t, attrT } = useTranslation();
  const ref = useRef(null);

  const pdfDownload = () => {
    setcapture(true);
    ref.current.style.borderWidth = '0px';
    const input = ref.current;
    html2canvas(input, {
      scale: 5,
      useCORS: true,
    }).then((canvas) => {
      const imageData = canvas.toDataURL('image.png');
      // console.log("imageData: ", imageData);
      // const a = document.createElement("a");
      // a.href = imageData;
      // a.download = "image.jpeg";
      // a.click();
      console.log([canvas.width, canvas.height]);
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: [canvas.width / 5, canvas.height / 5],
        compress: true,
      });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = 0;
      const imgY = 0;

      pdf.addImage(
        imageData,
        'PNG',
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio,
      );
      pdf.save(`${product.name}.pdf`);

      setcapture(false);
      router.back();
    });
  };

  useEffect(() => {
    if (capture) {
      setprogressbar('w-[55%]');
      setTimeout(() => {
        setprogressbar('w-[85%]');
      }, 4000);
    }
  }, [capture]);

  const size = useWindowSize();

  if (size.width < 1024) {
    return (
      <>
        <div className='mt-[50vh] py-1 text-center font-black bg-slate-300'>
          {t('flyer-desktop-hint')}
        </div>
      </>
    );
  }

  return (
    <div>
      <SWRConfig
        value={{
          fallback: {
            [`${product.baseSku}`]: product.ratings
              ? product.ratings.summary
              : {},
          },
        }}
      >
        {/*{capture && <div className='absolute z-10 h-[100%] w-[100vw] bg-[#4845354e] flex flex-col items-center justify-center'>*/}
        {/*  <div className='flex flex-row items-center'>*/}
        {/*    <p className='text-[35px] font-semibold text-[#ffffff] drop-shadow-lg mr-2'>{t('generating-flyer')}</p>*/}
        {/*    <div className='text-[35px] font-semibold text-[#fff] drop-shadow-lg rounded-full animate-bounce [animation-delay:-0.3s]'>.</div>*/}
        {/*    <div className='text-[35px] font-semibold text-[#fff] drop-shadow-lg rounded-full animate-bounce [animation-delay:-0.15s]'>.</div>*/}
        {/*    <div className='text-[35px] font-semibold text-[#fff] drop-shadow-lg rounded-full animate-bounce'>.</div>*/}
        {/*  </div>*/}

        {/*  <div className="mt-4 w-[24em] h-3 bg-blue-200 rounded-full shadow-md shadow-[#656251] border">*/}
        {/*    <div className={`transition-all ease-out duration-[2000ms] ${progressbar} h-full text-center text-xs text-white bg-green rounded-full`}>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</div>}*/}

        <div className={`${capture && 'h-[100vh] overflow-hidden blur-md'}`}>
          <div
            ref={ref}
            className={`${capture ? 'border-[0px] scale-[1]' : 'scale-[.35] border-[1px] border-cyan-800 mt-[-20vw] xl:mt-[-36vw]'}  pb-[70px] `}
          >
            <Detail
              flyer={true}
              preview={preview}
              product={product}
              heroImageClassName={`hidden absolute h-[260px] w-[450px] ${capture ? 'lg:h-[1400px] lg:w-[480px] right-[-100px]' : 'lg:h-[1500px] lg:w-[450px] right-[-70px]'} top-0 mt-[300px] lg:mt-[250px]`}
              related={``}
              heroTitle={Title}
              relatedTitle={t('relatedProducts')}
              relatedBtnLink={`/wein?filter=struktur--${product.attributes.struktur.value}`}
              relatedBtnText={t('seeMore')}
              section='wine'
              bgColor='bg-white'
              heroClassName='py-6 relative '
              heroImageAlign='text-center'
              showFooter={false}
              showNav={false}
            >
              <>
                <Container>
                  <div className='l gap-2 lg:grid lg:grid-cols-12 '>
                    <div className='col-span-7'>
                      <div
                        className='lg:text-[1.5rem] lg:font-normal '
                        dangerouslySetInnerHTML={{
                          __html: attrT(product, 'description'),
                        }}
                      />

                      <Structure
                        product={product}
                        className='mt-2 grid -translate-x-16 scale-[0.65] xs:-translate-x-9 xs:scale-[0.80] sm:scale-100 sm:transform-none lg:hidden'
                        borderColor='border-black'
                        bgColor='bg-black'
                        appellationImg={product.regionMap?.srcBlack}
                      />

                      <Accordion
                        title='Weitere Information'
                        mobileOnly
                        positionClassName='sm:mt-10'
                        classNameBody='pt-4'
                      >
                        <Attributes
                          wine={product}
                          className='lg:mt-14 lg:text-[1.5rem] ml-[-10px]'
                          color='black'
                        />
                        <Histogram
                          wine={product}
                          className={`mt-2 lg:mt-14 lg:max-w-sm `}
                          currentColor={'bg-black'}
                        />
                      </Accordion>
                    </div>
                  </div>

                  <div className='mt-[120px] mb-[-60px]  relative flex justify-between'>
                    {/* <Image src={signature} alt='' width={120} height={70} /> */}
                    <div>
                      <img src={`/static/edvin.png`} alt='' width={150} />
                      <p className='text-[25px] text-[#466c46] '>
                        <a href='https://edvinweine.ch/'>edvinweine.ch</a>
                      </p>
                    </div>
                    <div className='absolute bottom-0 right-0'>
                      <img
                        src={product.images[0].url}
                        alt=''
                        width={370}
                        height={700}
                      />
                    </div>
                  </div>

                  {product.images && product.images.length > 1 ? (
                    <div className='mb-14 print:hidden'>
                      <DynamicProductImageCarousel />
                    </div>
                  ) : (
                    <></>
                  )}
                </Container>
              </>
            </Detail>
          </div>
        </div>

        {}

        <button
          onClick={pdfDownload}
          className={`fixed top-[7vh] right-16 px-8 py-3 rounded-xl ${capture ? 'bg-slate-200 cursor-wait' : 'bg-slate-200 hover:bg-slate-300 active:bg-slate-400'} `}
        >
          {capture ? t('downloading-flyer') : t('download-flyer')}
        </button>
      </SWRConfig>
    </div>
  );
};

export default WineDetail;
